import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { ElfBaseStore } from '@/store/base-store.repository';
import { PaaMilestonesRepositoryFactory } from '@/store/paa-state/milestones-state/milestones.repository';
import { PaaMilestoneProps } from '@/store/paa-state/milestones-state/milestones.store';
import { PaaRepository } from '@/store/paa-state/paa-state.repository';
import { PaaMilestone, PaaMilestoneDto } from '@/types';
import { form } from '@/utils/form.utils';
import {
  paaMilestoneDraftValidationSchema,
  paaMilestoneFloawableValidationSchema,
  paaMilestoneFundingValidationSchema,
  paaMilestoneSlotsValidationSchema,
} from '@/validations/paa/paa-milestone.validation';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { select } from '@ngneat/elf';
import _ from 'lodash';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PaaMilestonesService implements OnDestroy {
  multiFormAccordionService = inject(MultiFormAccordionService);
  paaRepository = inject(PaaRepository);
  messageService = inject(MessageService);

  private storeSubscriptions: Subscription[] = [];

  initialize() {
    this.subscribeToMilestoneStores();
    this.storeSubscriptions.push(
      this.multiFormAccordionService.newStore$.subscribe(store => {
        this.subscribeToStore(store);
        this.calculateTotalEstimatedBudget();
      })
    );
    this.subscribeToStores();
  }

  subscribeToMilestoneStores() {
    const stores = this.multiFormAccordionService.getAllStores();
    this.storeSubscriptions = stores.map(store => {
      return store.pipe(select(state => state)).subscribe(() => {
        this.calculateTotalEstimatedBudget();
      });
    });
  }

  subscribeToStore(store?: ElfBaseStore) {
    const sub = store?.pipe(select(state => state)).subscribe(() => {
      this.calculateTotalEstimatedBudget();
    });
    this.storeSubscriptions.push(sub!);
  }

  subscribeToStores() {
    this.subscribeToMilestoneStores();
    this.storeSubscriptions.push(
      this.multiFormAccordionService.newStore$.subscribe(store => {
        this.subscribeToStore(store);
        this.calculateTotalEstimatedBudget();
      })
    );
    this.storeSubscriptions.push(
      this.multiFormAccordionService.storeRemoved$.subscribe(() => {
        this.calculateTotalEstimatedBudget();
      })
    );
  }

  calculateTotalEstimatedBudget() {
    const total = this.multiFormAccordionService.getAllStores().reduce((sum, store) => {
      return sum + ((store.state as PaaMilestoneProps)?.milestoneDto?.budgetEstime ?? 0);
    }, 0);
    this.paaRepository.set('totalEstimatedBudget', total);
  }

  getAllMilestoneDtos() {
    return this.multiFormAccordionService.getAllStores().map((store: ElfBaseStore<PaaMilestoneProps>): PaaMilestoneDto => {
      const repo = PaaMilestonesRepositoryFactory.create(store);
      return repo.get('milestoneDto');
    });
  }

  validateSlots(milestoneDto: PaaMilestoneDto, maxBudget = 0) {
    let isValid = true;
    const lotsErrors: Record<string, string[]>[] = [];
    const lots = milestoneDto.lots || [];

    // No slots present, considered valid
    if (lots.length === 0) {
      return { lotsErrors, isValid };
    }

    let budgetCount = 0;
    let definedBudgets = 0;
    let totalBudget = 0;

    lots.forEach((lot, i) => {
      const result = paaMilestoneSlotsValidationSchema.safeParse(lot);
      if (!result.success) {
        isValid = false;
        lotsErrors[i] = result.error.flatten().fieldErrors;
      } else {
        lotsErrors[i] = {};
      }

      if (typeof lot.budget === 'number') {
        definedBudgets++;
        totalBudget += lot.budget;
      }

      budgetCount++;
    });

    // Vérification de la définition partielle des budgets
    if (definedBudgets > 0 && definedBudgets < budgetCount) {
      isValid = false;
      for (let i = 0; i < budgetCount; i++) {
        if (typeof lots[i].budget !== 'number') {
          lotsErrors[i] = {
            ...lotsErrors[i],
            budget: ['Le budget est requis si au moins un budget est défini.'],
          };
        }
      }
    }

    // Vérification de la somme des budgets
    if (definedBudgets === budgetCount && totalBudget !== maxBudget) {
      isValid = false;
      for (let i = 0; i < budgetCount; i++) {
        lotsErrors[i] = {
          ...lotsErrors[i],
          budget: ["la somme des budgets des lots ne correspond pas au budget estimé de l'activité"],
        };
      }
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Le total des budgets doit être égal au budget autorisé.',
      });
    }

    return { lotsErrors, isValid };
  }

  validateFundings(milestoneDto: PaaMilestoneDto) {
    let isValid = true;
    const fundingsErrors: Record<string, string[]>[] = [];
    const fundings = milestoneDto.financements || [];

    if (fundings.length === 0) {
      return { fundingsErrors, isValid };
    }

    fundings.forEach((funding, i) => {
      const result = paaMilestoneFundingValidationSchema.safeParse(funding);
      if (!result.success) {
        isValid = false;
        fundingsErrors[i] = result.error.flatten().fieldErrors;
      } else {
        fundingsErrors[i] = {};
      }
    });

    return { fundingsErrors, isValid };
  }

  validateAllMilestoneDtos(mode: 'draft' | 'flowable', minDate?: Date): boolean {
    let isValid = true;

    this.multiFormAccordionService.getAllStores().forEach((store: ElfBaseStore<PaaMilestoneProps>, index) => {
      const repo = PaaMilestonesRepositoryFactory.create(store);
      const milestoneDto = repo.get<PaaMilestoneDto>('milestoneDto');

      const result =
        mode == 'draft'
          ? paaMilestoneDraftValidationSchema(minDate).safeParse(milestoneDto)
          : paaMilestoneFloawableValidationSchema(minDate).safeParse(milestoneDto);
      const slotsResult = this.validateSlots(milestoneDto, milestoneDto.budgetEstime);
      const fundingsResult = this.validateFundings(milestoneDto);

      if (!result.success || !slotsResult.isValid || !fundingsResult.isValid) {
        isValid = false;

        const fieldErrors = result.success ? {} : result.error.flatten().fieldErrors;
        repo.set('errors', fieldErrors);
        repo.set('slotErrors', slotsResult.lotsErrors);
        repo.set('fundingErrors', fundingsResult.fundingsErrors);

        MultiFormAccordionService.openPanelIfClosed(`mfa-${index}`);
        form.scrollToInvalidElement();
      } else {
        repo.set('errors', {});
      }
    });

    return isValid;
  }

  setMilestoneDto(response: PaaMilestone): void {
    const newStore = this.multiFormAccordionService._formFactory?.(response?.id.toString());
    if (!newStore) {
      console.error(`Could not create store for milestone with id: ${response.id}`);
      return;
    }

    const root = this.multiFormAccordionService.root;
    if ('children' in root) {
      root.children.push({ store: newStore });
    }

    this.multiFormAccordionService.newStore$.next(newStore);

    const repo = PaaMilestonesRepositoryFactory.create(newStore);
    repo.setMilestoneDto(response);
  }

  setMilestoneDtos(responses: PaaMilestone[]): void {
    responses.forEach(r => {
      this.setMilestoneDto(r);
    });
  }

  altered(): boolean {
    return this.multiFormAccordionService.getAllStores().some(store => {
      const repo = PaaMilestonesRepositoryFactory.create(store);
      const current = repo.get<PaaMilestoneDto>('milestoneDto');
      const initial = repo.get<PaaMilestoneDto>('initialMilestoneDto');

      if (!initial) return true;

      const currentWithout = _.omit(current, ['budgetEstime']);
      const initialWithout = _.omit(initial, ['budgetEstime']);

      return !_.isEqual(currentWithout, initialWithout);
    });
  }

  clearAttribute(attribute: string) {
    this.multiFormAccordionService.getAllStores().forEach(store => {
      const repo = PaaMilestonesRepositoryFactory.create(store);
      repo.setNested(attribute, undefined);
    });
  }

  clearNestedDtoAttribute(attribute: string) {
    this.multiFormAccordionService.getAllStores().forEach(store => {
      const repo = PaaMilestonesRepositoryFactory.create(store);
      repo.setNested('milestoneDto', attribute, undefined);
    });
  }

  ngOnDestroy() {
    this.storeSubscriptions.forEach(sub => sub.unsubscribe());
  }
}
